import React, {useState, useEffect, useRef} from 'react';
import {useAiAvailable} from '../contexts/AiAvailability';
import {textToSpeech} from '../helpers/externalApi';
import {
  fetchDrillCards,
  saveVerbDrillProgress,
} from '../helpers/hebrewVerbsApi';

const SWIPE_THRESHOLD = 80;

const DRILL_MODES = [
  {value: 'inf-to-form', label: 'Infinitive → Conjugated form'},
  {value: 'form-to-identify', label: 'Conjugated form → Identify'},
];

export const HebrewVerbDrill = () => {
  const {aiAvailable} = useAiAvailable();
  const [mode, setMode] = useState('inf-to-form');
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [pronounceLoading, setPronounceLoading] = useState(false);

  const touchStartRef = useRef(null);
  const wasDraggingRef = useRef(false);

  const handlePronounce = async (text) => {
    if (!aiAvailable || !text || pronounceLoading) return;
    setPronounceLoading(true);
    try {
      const blob = await textToSpeech({text});
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play();
    } catch (err) {
      console.warn('TTS failed:', err.message);
    } finally {
      setPronounceLoading(false);
    }
  };

  const loadCards = (selectedMode) => {
    setLoading(true);
    fetchDrillCards({mode: selectedMode, limit: 100})
      .then((data) => {
        setCards(data.cards || []);
        setCurrentIndex(0);
        setFlipped(false);
        setCorrect(0);
        setIsComplete(false);
        setDragX(0);
      })
      .catch(() => {
        setCards([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCards(mode);
  }, [mode]);

  const currentCard = cards[currentIndex];

  const advance = (knewIt) => {
    if (currentCard) {
      saveVerbDrillProgress({
        verbFormId: currentCard.formId,
        correct: knewIt,
      })
        .then(() => {})
        .catch(() => {});
    }
    if (knewIt) setCorrect((prev) => prev + 1);
    setFlipped(false);
    setDragX(0);

    if (currentIndex + 1 >= cards.length) {
      setIsComplete(true);
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleCardTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    touchStartRef.current = {x: e.touches[0].clientX, y: e.touches[0].clientY};
    wasDraggingRef.current = false;
  };

  const handleCardTouchMove = (e) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;
    if (Math.abs(dx) > Math.abs(dy) * 1.3) {
      const clamped = Math.sign(dx) * Math.min(Math.abs(dx), 150);
      setDragX(clamped);
      if (Math.abs(dx) > 8) wasDraggingRef.current = true;
    }
  };

  const handleCardTouchEnd = () => {
    const savedDragX = dragX;
    const wasDrag = wasDraggingRef.current;
    touchStartRef.current = null;
    wasDraggingRef.current = false;
    setDragX(0);
    if (wasDrag && Math.abs(savedDragX) >= SWIPE_THRESHOLD) {
      advance(savedDragX > 0);
    }
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setFlipped(false);
    setCorrect(0);
    setIsComplete(false);
  };

  if (loading) {
    return (
      <div className="study-container">
        <div className="study-empty">
          <p>Loading verb drills...</p>
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="study-container">
        <div className="study-empty">
          <h2>No verbs to practice</h2>
          <p>Import some verbs from Pealim first.</p>
        </div>
      </div>
    );
  }

  if (isComplete) {
    const pct = cards.length > 0 ? Math.round((correct / cards.length) * 100) : 0;
    const incorrect = cards.length - correct;
    return (
      <div className="study-container">
        <div className="study-complete">
          <div style={{fontSize: 52}}>
            {pct >= 80 ? 'Great work' : pct >= 50 ? 'Keep going' : 'Review again'}
          </div>
          <div className="study-score">{pct}%</div>
          <h2>Drill Complete!</h2>
          <p>You got {correct} out of {cards.length} correct.</p>
          <div className="study-complete-stats">
            <div className="study-stat">
              <span className="study-stat-value" style={{color: 'var(--green)'}}>{correct}</span>
              <span className="study-stat-label">Correct</span>
            </div>
            <div className="study-stat">
              <span className="study-stat-value" style={{color: 'var(--red)'}}>{incorrect}</span>
              <span className="study-stat-label">Incorrect</span>
            </div>
          </div>
          <button type="button" className="btn btn-primary" style={{width: '100%', marginTop: 12}} onClick={handleRestart}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const progress = cards.length > 0 ? (currentIndex / cards.length) * 100 : 0;
  const showKnow = dragX > 30;
  const showSkip = dragX < -30;

  const renderFront = () => {
    if (mode === 'form-to-identify') {
      return (
        <>
          <div className="verb-form-display font-hebrew">{currentCard?.prompt}</div>
          {currentCard?.promptTransliteration && (
            <div className="verb-form-tense">{currentCard.promptTransliteration}</div>
          )}
          <div className="verb-form-hint">Tap to reveal which form this is</div>
        </>
      );
    }
    return (
      <>
        <div className="verb-form-display verb-prompt-text">{currentCard?.prompt}</div>
        {currentCard?.promptTense && (
          <div className="verb-form-tense verb-form-id">
            <span className="verb-form-id-tense">{currentCard.promptTense}</span>
            {currentCard?.promptPronoun && (
              <span className="verb-form-id-pronoun font-hebrew">{currentCard.promptPronoun}</span>
            )}
          </div>
        )}
        {currentCard?.promptHebrew && (
          <div className="verb-infinitive font-hebrew">{currentCard.promptHebrew}</div>
        )}
        <div className="verb-form-hint">Tap to reveal the conjugated form</div>
      </>
    );
  };

  const renderBack = () => {
    const pronounceText = mode === 'form-to-identify'
      ? currentCard?.answerHebrew
      : currentCard?.answer;
    const PronounceBtn = aiAvailable && pronounceText ? (
      <button
        type="button"
        className="verb-pronounce-btn"
        onClick={(e) => {
          e.stopPropagation();
          handlePronounce(pronounceText);
        }}
        disabled={pronounceLoading}
        aria-label="Pronounce"
      >
        {pronounceLoading ? '⏳' : '🔊'}
      </button>
    ) : null;

    if (mode === 'form-to-identify') {
      return (
        <>
          <div className="verb-form-tense verb-answer-label verb-form-id">
            <span className="verb-form-id-tense">{currentCard?.answerTense || currentCard?.answer}</span>
            {currentCard?.answerPronoun && (
              <span className="verb-form-id-pronoun font-hebrew">{currentCard.answerPronoun}</span>
            )}
          </div>
          <div className="verb-infinitive-row">
            <div className="verb-infinitive font-hebrew">{currentCard?.answerHebrew}</div>
            {PronounceBtn}
          </div>
          <div className="verb-transliteration">{currentCard?.answerTransliteration}</div>
          <div className="verb-en-meaning">{currentCard?.answerEn}</div>
        </>
      );
    }
    return (
      <>
        <div className="verb-infinitive-row">
          <div className="verb-form-display font-hebrew">{currentCard?.answer}</div>
          {PronounceBtn}
        </div>
        <div className="verb-transliteration">{currentCard?.answerTransliteration}</div>
        {currentCard?.answerEn && (
          <div className="verb-en-meaning">{currentCard.answerEn}</div>
        )}
      </>
    );
  };

  return (
    <div className="study-container">
      <div className="verbs-drill-mode-row">
        {DRILL_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            className={`btn btn-sm${mode === m.value ? ' btn-primary' : ' btn-secondary'}`}
            onClick={() => setMode(m.value)}
            disabled={loading}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="study-progress">
        <div className="study-progress-bar">
          <div className="study-progress-fill" style={{width: `${progress}%`}} />
        </div>
        <span className="study-progress-text">{currentIndex + 1} / {cards.length}</span>
      </div>

      {flipped && (
        <div className="study-swipe-hint">
          <span className="study-swipe-hint-know">Know it</span>
          <span className="study-swipe-hint-skip">Skip</span>
        </div>
      )}

      <div
        className="flashcard-shell"
        onTouchStart={handleCardTouchStart}
        onTouchMove={handleCardTouchMove}
        onTouchEnd={handleCardTouchEnd}
        onTouchCancel={handleCardTouchEnd}
      >
        <div className={`flashcard-swipe-indicator flashcard-swipe-know${showKnow ? ' visible' : ''}`} aria-hidden="true">
          Know
        </div>
        <div className={`flashcard-swipe-indicator flashcard-swipe-skip${showSkip ? ' visible' : ''}`} aria-hidden="true">
          Skip
        </div>

        <div
          className={`flashcard${flipped ? ' flipped' : ''}`}
          onClick={() => !wasDraggingRef.current && setFlipped(!flipped)}
          style={dragX !== 0 ? {
            transform: `rotate(${dragX * 0.04}deg) translateX(${dragX * 0.25}px)`,
            transition: 'none',
          } : {}}
        >
          <div className="flashcard-inner">
            <div className="flashcard-front">
              {renderFront()}
            </div>
            <div className="flashcard-back">
              {renderBack()}
            </div>
          </div>
        </div>
      </div>

      <div className="study-card-actions">
        <button type="button" className="btn btn-secondary" onClick={() => advance(false)}>
          Skip
        </button>
        <button type="button" className="btn btn-primary" onClick={() => advance(true)}>
          Know
        </button>
      </div>
    </div>
  );
};
